import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  useCreateMonetizeKnowledgeLeadMutation,
  CreateMonetizeKnowledgeRequest,
} from "../../app/api/monetizeKnowledgeApi";

interface FormState {
  firstName: string;
  email: string;
  phone: string;
}

interface MonetizeKnowledgeProps {
  eventDate?: string;
  venue?: string;
  city?: string;
}

const MonetizeKnowledge: React.FC<MonetizeKnowledgeProps> = ({
  eventDate = new Date().toISOString(),
  venue,
  city,
}) => {
  // Initialize the RTK Query mutation hook
  const [createLead, { isLoading }] = useCreateMonetizeKnowledgeLeadMutation();

  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  const [form, setForm] = useState<FormState>({
    firstName: "",
    email: "",
    phone: "",
  });

  // isSubmitting state is now derived from RTK Query's isLoading
  const isSubmitting = isLoading;
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const computeTimeLeft = useCallback(() => {
    const now = Date.now();
    const eventTime = new Date(eventDate).getTime();
    const distance = eventTime - now;

    if (distance > 0) {
      return {
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor(
          (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        ),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000),
      };
    }

    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }, [eventDate]);

  useEffect(() => {
    setTimeLeft(computeTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(computeTimeLeft());
    }, 1000);
    return () => clearInterval(timer);
  }, [computeTimeLeft]);

  const formatEventDate = useCallback(() => {
    const date = new Date(eventDate);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }, [eventDate]);

  const formatEventTime = useCallback(() => {
    const date = new Date(eventDate);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }, [eventDate]);

  const isValidEmail = useCallback((email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }, []);

  const updateField = useCallback((field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setStatusMessage(null);

      // --- Validation ---
      if (!form.firstName || form.firstName.trim().length < 2) {
        setStatusMessage("Please enter your first name.");
        return;
      }

      if (!form.email || !isValidEmail(form.email)) {
        setStatusMessage("Please enter a valid email address.");
        return;
      }

      const rawPhone = form.phone ?? "";
      const phoneDigits = rawPhone.replace(/\D/g, "");
      if (!rawPhone || phoneDigits.length < 10) {
        setStatusMessage("Please enter a valid phone number.");
        return;
      }

      // --- Prepare API Payload (Aligned with Backend) ---
      const payload: CreateMonetizeKnowledgeRequest = {
        firstName: form.firstName.trim(),
        email: form.email.toLowerCase().trim(),
        phone: phoneDigits,
        eventDate: eventDate,
        eventCity: city,
        venue: venue,
      };

      try {
        await createLead(payload).unwrap();

        if (!mountedRef.current) return;

        // Successful submission handling
        setStatusMessage(
          "🎉 Success! Your seat is reserved. Check your email (and spam/promotions) for event details."
        );
        setForm({ firstName: "", email: "", phone: "" });

        // Scroll to the message after success
        const el = document.getElementById("register-form");
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      } catch (err: any) {
        console.error("Registration error:", err);
        if (!mountedRef.current) return;

        const errorMsg =
          err.data?.message ||
          err.message ||
          "Something went wrong. Please try again.";

        // Check for 409 Duplicate Registration error (as implemented in the backend)
        if (err.status === 409) {
          setStatusMessage(
            "⚠️ You are already registered for this event date! Check your email."
          );
        } else {
          setStatusMessage(errorMsg);
        }
      }
    },
    [form, isValidEmail, createLead, eventDate, city, venue]
  );

  const Box: React.FC<{ label: string; value: number }> = ({
    label,
    value,
  }) => (
    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-2 sm:p-3 text-center border border-white/20 flex flex-col items-center justify-center w-full aspect-square sm:aspect-auto sm:min-w-[68px]">
      <div
        className="text-xl sm:text-4xl font-extrabold leading-none"
        style={{ fontVariantNumeric: "tabular-nums" }}
      >
        {String(value).padStart(2, "0")}
      </div>
      <div className="text-[10px] sm:text-xs uppercase tracking-wide mt-1 text-white/80">
        {label}
      </div>
    </div>
  );

  return (
    <div className="w-full min-h-screen bg-white text-brandNavy font-sans">
      {/* Hero */}
      <section className="bg-brandNavy text-white overflow-hidden relative">
        {/* Background pattern could go here */}
        <div className="relative max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
          <div className="text-center mb-8 sm:mb-12">
            <div className="inline-block bg-brandRed px-4 sm:px-6 py-2 rounded-full text-xs sm:text-lg font-bold mb-6 shadow-lg animate-pulse tracking-wide">
              🔥 ABUJA MONETIZE YOUR KNOWLEDGE WORKSHOP
            </div>

            <h1 className="text-3xl sm:text-5xl md:text-6xl font-extrabold leading-tight mb-4 sm:mb-6">
              Turn The Knowledge In Your Head
              <br className="hidden sm:block" />
              Into <span className="text-yellow-400">Cash In Your Hand</span>
            </h1>

            <p className="text-base sm:text-xl text-white/90 max-w-3xl mx-auto mb-8 leading-relaxed px-2">
              One Saturday. One System. Discover how to build ₦300K–₦500K
              monthly online income from your existing skills — without quitting
              your job.
            </p>

            {/* Countdown - Fixed Responsiveness */}
            <div className="max-w-xl mx-auto mb-8 sm:mb-10">
              <p className="text-xs sm:text-sm font-semibold mb-3 uppercase tracking-wide text-white/90">
                Event Starts In:
              </p>

              {/* Changed from flex overflow to grid for better mobile fit */}
              <div className="grid grid-cols-4 gap-2 sm:gap-4 px-2 sm:px-0">
                <Box label="Days" value={timeLeft.days} />
                <Box label="Hrs" value={timeLeft.hours} />
                <Box label="Mins" value={timeLeft.minutes} />
                <Box label="Secs" value={timeLeft.seconds} />
              </div>
            </div>

            {/* Event Details - Fixed Responsiveness (Stack on mobile) */}
            <div className="flex flex-col md:flex-row gap-4 md:gap-0 justify-center items-center text-lg sm:text-2xl mb-8 sm:mb-10 bg-white/5 md:bg-transparent p-4 md:p-0 rounded-2xl md:rounded-none border border-white/10 md:border-none w-full max-w-md md:max-w-none mx-auto">
              <div className="flex items-center gap-2 text-base sm:text-lg">
                <svg
                  className="w-5 h-5 text-yellow-400 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span className="whitespace-nowrap">{formatEventDate()}</span>
              </div>

              {/* Separator - Hidden on mobile */}
              <div className="hidden md:block w-px h-6 bg-white/30 mx-6" />

              <div className="flex items-center gap-2 text-base sm:text-lg">
                <svg
                  className="w-5 h-5 text-yellow-400 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="whitespace-nowrap">{formatEventTime()}</span>
              </div>

              {/* Separator - Hidden on mobile */}
              <div className="hidden md:block w-px h-6 bg-white/30 mx-6" />

              <div className="flex items-center gap-2 text-base sm:text-lg text-center md:text-left">
                <svg
                  className="w-5 h-5 text-yellow-400 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <span>
                  {venue}, {city}
                </span>
              </div>
            </div>

            <button
              onClick={() => {
                const el = document.getElementById("register-form");
                el?.scrollIntoView({ behavior: "smooth", block: "center" });
              }}
              className="w-full sm:w-auto inline-block bg-brandRed text-white px-6 sm:px-12 py-4 sm:py-5 rounded-lg font-bold text-lg sm:text-xl hover:bg-red-700 transition-all transform hover:scale-[1.02] shadow-2xl border-2 border-transparent hover:border-red-400"
            >
              Reserve My FREE Seat Now →
            </button>

            <p className="text-sm sm:text-lg text-white/70 mt-4 sm:mt-6">
              ⚡ Only 100 seats available •{" "}
              <span className="font-semibold text-white">12 seats left</span>
            </p>
          </div>
        </div>
      </section>

      {/* Problems Section */}
      <section className="py-12 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-4xl font-bold text-brandNavy mb-3">
              Does This Sound Like You?
            </h2>
            <p className="text-base sm:text-xl text-mutedGray max-w-3xl mx-auto">
              You're not alone. These are the struggles every ambitious Nigerian
              professional faces...
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {[
              {
                icon: "😰",
                problem:
                  "Your salary disappears before the 20th. The cost of living keeps rising faster than your income.",
              },
              {
                icon: "😤",
                problem:
                  "You feel pressure to support family, but your job barely covers your own expenses.",
              },
              {
                icon: "😵",
                problem:
                  "You want to start something online, but every idea feels confusing and overwhelming.",
              },
              {
                icon: "😓",
                problem:
                  "You've tried crypto, mini-importation, affiliate marketing... nothing has worked consistently.",
              },
              {
                icon: "⏰",
                problem:
                  "Your job already drains your time and energy. You barely have time to think, let alone build a business.",
              },
              {
                icon: "🤔",
                problem:
                  "You have skills and knowledge but don't know how to package them into a profitable business.",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-6 sm:p-8 shadow-md hover:shadow-xl transition-shadow border border-gray-100"
              >
                <div className="text-4xl sm:text-5xl mb-4">{item.icon}</div>
                <p className="text-mutedGray leading-relaxed text-base sm:text-lg">
                  {item.problem}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-10 sm:mt-16 max-w-4xl mx-auto bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-6 sm:p-12 border border-red-100">
            <h3 className="text-2xl sm:text-3xl font-bold text-brandNavy mb-4 text-center">
              Here's The Truth...
            </h3>
            <p className="text-base sm:text-lg text-mutedGray leading-relaxed text-center">
              <strong className="text-brandRed">It's not your fault.</strong>{" "}
              You simply haven't had a system that matches your lifestyle and
              your strengths. You've been guessing instead of following a proven
              framework. That changes today.
            </p>
          </div>
        </div>
      </section>

      {/* D.B.C. Section */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <div className="inline-block bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-bold mb-3 tracking-wide">
              THE D.B.C. FORMULA™
            </div>
            <h2 className="text-2xl sm:text-4xl font-bold text-brandNavy mb-4">
              What You'll Discover At This Event
            </h2>
            <p className="text-base sm:text-xl text-mutedGray max-w-3xl mx-auto">
              This isn't theory. This is the exact system Nigerian professionals
              are using to build ₦300K-₦500K monthly online income.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8 max-w-6xl mx-auto mb-12">
            {/* 1 */}
            <div className="relative pt-4 md:pt-0">
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 md:-top-4 md:-left-4 md:translate-x-0 md:translate-y-0 w-12 h-12 sm:w-16 sm:h-16 bg-purple-600 text-white rounded-full flex items-center justify-center text-xl font-bold shadow-lg z-10">
                1
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-white rounded-2xl p-6 sm:p-8 pt-10 md:pt-12 shadow-xl border border-purple-100 h-full">
                <h3 className="text-xl sm:text-2xl font-bold text-brandNavy mb-3 text-center md:text-left">
                  DISCOVER
                </h3>
                <p className="text-mutedGray mb-4 leading-relaxed text-sm sm:text-base text-center md:text-left">
                  Find your profitable sweet spot at the intersection of PASSION
                  × SKILL × MARKET DEMAND
                </p>
                <ul className="space-y-3 text-mutedGray text-sm sm:text-base">
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 font-bold mt-0.5">✓</span>
                    <span>The PRO Audit framework</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 font-bold mt-0.5">✓</span>
                    <span>Sweet Spot Formula</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 font-bold mt-0.5">✓</span>
                    <span>6-point validation checklist</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 font-bold mt-0.5">✓</span>
                    <span>Offer creation blueprint</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* 2 */}
            <div className="relative pt-4 md:pt-0">
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 md:-top-4 md:-left-4 md:translate-x-0 md:translate-y-0 w-12 h-12 sm:w-16 sm:h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold shadow-lg z-10">
                2
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-6 sm:p-8 pt-10 md:pt-12 shadow-xl border border-blue-100 h-full">
                <h3 className="text-xl sm:text-2xl font-bold text-brandNavy mb-3 text-center md:text-left">
                  BUILD
                </h3>
                <p className="text-mutedGray mb-4 leading-relaxed text-sm sm:text-base text-center md:text-left">
                  Create authority and grow your audience using free organic
                  strategies
                </p>
                <ul className="space-y-3 text-mutedGray text-sm sm:text-base">
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 font-bold mt-0.5">✓</span>
                    <span>Visibility Engine system</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 font-bold mt-0.5">✓</span>
                    <span>WhatsApp & Email growth</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 font-bold mt-0.5">✓</span>
                    <span>Lead Magnet mastery</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 font-bold mt-0.5">✓</span>
                    <span>7-Day nurture sequence</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* 3 */}
            <div className="relative pt-4 md:pt-0">
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 md:-top-4 md:-left-4 md:translate-x-0 md:translate-y-0 w-12 h-12 sm:w-16 sm:h-16 bg-green-600 text-white rounded-full flex items-center justify-center text-xl font-bold shadow-lg z-10">
                3
              </div>
              <div className="bg-gradient-to-br from-green-50 to-white rounded-2xl p-6 sm:p-8 pt-10 md:pt-12 shadow-xl border border-green-100 h-full">
                <h3 className="text-xl sm:text-2xl font-bold text-brandNavy mb-3 text-center md:text-left">
                  CONVERT
                </h3>
                <p className="text-mutedGray mb-4 leading-relaxed text-sm sm:text-base text-center md:text-left">
                  Master selling without feeling "salesy" using automation and
                  psychology
                </p>
                <ul className="space-y-3 text-mutedGray text-sm sm:text-base">
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 font-bold mt-0.5">✓</span>
                    <span>Automated sales systems</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 font-bold mt-0.5">✓</span>
                    <span>Nigerian buyer psychology</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 font-bold mt-0.5">✓</span>
                    <span>High-converting sales assets</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 font-bold mt-0.5">✓</span>
                    <span>Payment systems & scaling</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-brandNavy to-brandLightNavy text-white rounded-2xl p-6 sm:p-12 max-w-5xl mx-auto shadow-2xl">
            <h3 className="text-xl sm:text-3xl font-bold mb-6 text-center">
              By The End Of This Saturday, You'll Have:
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {[
                "Crystal-clear direction on what online business to build",
                "A validated offer people actually want to pay for",
                "The exact roadmap to reach ₦300K-₦500K monthly",
                "Proven strategies you can start implementing Monday morning",
                "Templates and frameworks you can use immediately",
                "Confidence that you CAN build online income without quitting",
              ].map((benefit, i) => (
                <div key={i} className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-400 flex-shrink-0 mt-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-sm sm:text-lg">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Register Form */}
      <section id="register-form" className="py-12 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto w-full px-4 sm:px-6">
          <div className="bg-gradient-to-br from-brandNavy to-brandLightNavy text-white rounded-3xl p-6 sm:p-12 shadow-2xl">
            <div className="text-center mb-6 sm:mb-8">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
                Reserve Your FREE Seat Now
              </h2>

              <div className="inline-block bg-brandRed px-4 py-1.5 rounded-full text-sm font-bold animate-pulse">
                ⚡ Only 12 Seats Left
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    First Name *
                  </label>
                  <input
                    required
                    type="text"
                    value={form.firstName}
                    onChange={(e) => updateField("firstName", e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border-2 border-white/20 bg-white/10 text-white placeholder-white/60 focus:border-white focus:outline-none transition-colors"
                    placeholder="Your first name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Phone Number *
                  </label>
                  <input
                    required
                    inputMode="tel"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => updateField("phone", e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border-2 border-white/20 bg-white/10 text-white placeholder-white/60 focus:border-white focus:outline-none transition-colors"
                    placeholder="+234 800 000 0000"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Email Address *
                </label>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => updateField("email", e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border-2 border-white/20 bg-white/10 text-white placeholder-white/60 focus:border-white focus:outline-none transition-colors"
                  placeholder="you@example.com"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-brandRed text-white py-3 sm:py-4 rounded-lg font-bold text-lg hover:bg-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-xl mt-4"
              >
                {isSubmitting ? "Processing..." : "Complete FREE Registration"}
              </button>

              {statusMessage && (
                <div className="bg-white/10 border border-white/20 rounded-lg p-4 text-center mt-4">
                  <p className="text-white text-sm sm:text-base font-medium">
                    {statusMessage}
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-12 sm:py-20 bg-brandNavy text-white border-t border-white/10">
        <div className="max-w-5xl mx-auto w-full px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-4xl font-bold mb-8 sm:mb-10">
            One Saturday. One Decision. Your Financial Future.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10 text-left">
            <div className="bg-red-900/20 rounded-2xl p-6 sm:p-8 border border-red-500/50">
              <div className="text-3xl mb-4">❌</div>
              <h3 className="text-xl font-bold mb-3">Option 1: Do Nothing</h3>
              <ul className="space-y-2 text-white/80 text-sm sm:text-base">
                <li>• Still struggling financially in 6 months</li>
                <li>• Watching others succeed online while you stay stuck</li>
                <li>• Same salary pressure, same stress</li>
                <li>• Wondering "what if I had just taken action?"</li>
              </ul>
            </div>

            <div className="bg-green-900/20 rounded-2xl p-6 sm:p-8 border border-green-500/50">
              <div className="text-3xl mb-4">✅</div>
              <h3 className="text-xl font-bold mb-3">Option 2: Show Up</h3>
              <ul className="space-y-2 text-green-100 text-sm sm:text-base">
                <li>• Clear direction by Sunday</li>
                <li>• First client within 30 days</li>
                <li>• ₦100K-₦300K monthly income within 90 days</li>
                <li>• Financial freedom and peace of mind</li>
              </ul>
            </div>
          </div>

          <p className="text-lg sm:text-2xl mb-8 leading-relaxed max-w-3xl mx-auto">
            The difference between these two futures?{" "}
            <strong className="text-yellow-400 block sm:inline mt-2 sm:mt-0">
              One Saturday. One decision.
            </strong>
          </p>

          <button
            onClick={() => {
              const el = document.getElementById("register-form");
              el?.scrollIntoView({ behavior: "smooth", block: "center" });
            }}
            className="w-full sm:w-auto inline-block bg-brandRed text-white px-8 sm:px-12 py-4 sm:py-5 rounded-lg font-bold text-lg sm:text-2xl hover:bg-red-700 transition-all transform hover:scale-[1.02] shadow-2xl"
          >
            Yes, Reserve My Seat Now
          </button>

          <p className="text-sm sm:text-base text-white/50 mt-6">
            See you on {formatEventDate()} at {venue}, {city}
          </p>
        </div>
      </section>
    </div>
  );
};

export default MonetizeKnowledge;
